import { FC, useCallback } from "react";
import { Avatar, AvatarGroup, ButtonBase, Grid } from "@mui/material";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import { BillingExplorerCSVColumnItem } from "@/store/resource-explorer/selectors/current-resource-explorer/csv/possible-columns/utils/types";
import { ProviderIcon } from "@/components/common/ProviderIcon";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { CsvSelectedPossibleColumns } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/csv-v2/dialog/utils/types";

interface CsvDownloadDialogColumnItemProps
  extends BillingExplorerCSVColumnItem {
  keyValue: string;
  checked: boolean;
  onSelectedColumnsChange(data: CsvSelectedPossibleColumns): void;
  showProviders: boolean;
  disabled?: boolean;
}

export const CsvDownloadDialogColumnItem: FC<
  CsvDownloadDialogColumnItemProps
> = ({
  label,
  checked,
  providers,
  keyValue,
  onSelectedColumnsChange,
  showProviders,
  disabled,
}) => {
  const isMoreThanLimit = providers?.length ?? 0 > 5;
  const { avatarSize, iconSize, spacing } = sizes;

  const checkboxChangeHandler = useCallback(() => {
    if (disabled) {
      return;
    }
    onSelectedColumnsChange({
      [keyValue]: !checked,
    });
  }, [keyValue, disabled, onSelectedColumnsChange, checked]);

  return (
    <Grid item xs={4} minWidth={260}>
      <ButtonBase
        onClick={checkboxChangeHandler}
        disabled={disabled}
        sx={{
          display: "flex",
          alignItems: "center",
          border: 1,
          borderRadius: 2,
          borderColor: "grey.400",
          px: 1,
          width: "100%",
          justifyContent: "flex-start",
          textAlign: "left",
          "&:hover": {
            backgroundColor: "grey.100",
          },
        }}
      >
        <Checkbox size="small" checked={checked} disabled={disabled} />
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          overflow="hidden"
        >
          <TypographyWithTooltip
            title={label}
            variant="body2"
            fontWeight="medium"
          />

          <AvatarGroup
            spacing={spacing}
            max={5}
            sx={{
              ...avatarGroupStyles,
              "& .MuiAvatar-root:nth-of-type(1)": {
                height: isMoreThanLimit ? avatarSize - 1 : avatarSize,
                width: isMoreThanLimit ? avatarSize - 1 : avatarSize,
                fontSize: isMoreThanLimit ? iconSize - 2 : iconSize,
              },
            }}
          >
            {showProviders &&
              providers?.map((provider) => {
                if (provider === "global") {
                  return null;
                }
                return (
                  <Avatar
                    key={provider}
                    sx={{
                      bgcolor: "white",
                      height: avatarSize,
                      width: avatarSize,
                    }}
                  >
                    <ProviderIcon
                      provider={provider}
                      key={provider}
                      width={iconSize}
                    />
                  </Avatar>
                );
              })}
          </AvatarGroup>
        </Stack>
      </ButtonBase>
    </Grid>
  );
};
const sizes = {
  avatarSize: 23,
  iconSize: 13,
  spacing: 10,
};

const avatarGroupStyles = {
  "& >.MuiAvatar-root": {
    border: 1,
    borderColor: "grey.200",
  },
};
